import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { collection, orderBy, query, where } from 'firebase/firestore';
import { Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import type { OperationDoc } from '@asm/shared';
import { db } from '@/lib/firebase';
import { useLiveQuery } from '@/lib/hooks';
import { useSession } from '@/auth/AuthProvider';
import { isStaleAiOperation, useActivityNow } from './activityState';
import { api, callableMessage } from '@/lib/callables';

/**
 * A slim always-there progress strip under the topbar that appears while any of the
 * user's operations are running — so long jobs (sync, push, AI, uploads) are always
 * visible and followable, with a live progress bar.
 */
export function ActivityBar() {
  const { uid } = useSession();
  const now = useActivityNow();
  const running = useLiveQuery<OperationDoc>(
    useMemo(
      () =>
        uid
          ? query(
              collection(db, 'operations'),
              where('startedBy', '==', uid),
              where('status', '==', 'running'),
              orderBy('startedAt', 'desc'),
            )
          : null,
      [uid],
    ),
    `running-ops-${uid}`,
  );

  const active = running.rows.filter((row) => !isStaleAiOperation(row.data, now));
  const topRow = active[0] ?? null;
  const cancel = useMutation({
    mutationFn: () => {
      if (!topRow) throw new Error('This activity already finished.');
      return api.aiTranslationCancel({ operationId: topRow.id });
    },
    onSuccess: () => toast.info('Cancelling AI translation…', { description: 'The current chunk may take a few seconds to stop.' }),
    onError: (error) => toast.error('Couldn’t cancel translation', { description: callableMessage(error) }),
  });
  if (!topRow) return null;
  const top = topRow.data;
  const pct = top.progress && top.progress.total > 0 ? Math.round((top.progress.done / top.progress.total) * 100) : null;

  return (
    <div className="border-b bg-primary/5">
      <div className="flex min-w-0 items-center gap-2 px-3 py-1.5 sm:gap-3 sm:px-4">
        <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" />
        <span className="truncate text-[12px] font-medium">
          {top.label}
          {top.progress ? ` · ${top.progress.done}/${top.progress.total}` : ''}
          {active.length > 1 ? `  (+${active.length - 1} more)` : ''}
        </span>
        <div className="ml-auto hidden h-1 w-40 overflow-hidden rounded-full bg-primary/15 sm:block">
          <div
            className={pct === null ? 'h-full w-1/3 animate-pulse rounded-full bg-primary' : 'h-full rounded-full bg-primary transition-[width] duration-500'}
            style={pct === null ? undefined : { width: `${pct}%` }}
          />
        </div>
        {top.type === 'ai-translate' && (
          <button
            type="button"
            onClick={() => cancel.mutate()}
            disabled={cancel.isPending}
            className="inline-flex h-6 shrink-0 items-center gap-1 rounded-md border bg-background px-2 text-[11px] font-medium hover:bg-muted disabled:opacity-60"
            title="Cancel AI translation"
          >
            {cancel.isPending ? <Loader2 className="size-3 animate-spin" /> : <X className="size-3" />}
            <span className="hidden sm:inline">Cancel</span>
          </button>
        )}
      </div>
    </div>
  );
}
